// src/ChangePasswordModal.js
import React, { useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import logger from "../../../utils/logger";
import config from "../../../config";
import "./ChangePasswordModal.css";

const ChangePasswordModal = ({ isOpen, onClose, user }) => {
  const { getAccessToken } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const validatePassword = (password) => {
    const requirements = [
      { test: password.length >= 8, message: "At least 8 characters" },
      { test: /[a-z]/.test(password), message: "One lowercase letter" },
      { test: /[A-Z]/.test(password), message: "One uppercase letter" },
      { test: /[0-9]/.test(password), message: "One number" },
      {
        test: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
        message: "One special character",
      },
    ];
    return requirements;
  };

  const getPasswordStrength = (password) => {
    const requirements = validatePassword(password);
    const metCount = requirements.filter((r) => r.test).length;
    if (metCount <= 2) return "weak";
    if (metCount <= 4) return "medium";
    return "strong";
  };

  const handleChangePassword = async () => {
    try {
      setPasswordError(null);
      setPasswordSuccess(false);
      setIsChangingPassword(true);

      // Validate password requirements
      const requirements = validatePassword(newPassword);
      const allMet = requirements.every((req) => req.test);

      if (!allMet) {
        setPasswordError("Password does not meet all requirements");
        return;
      }

      // Check if passwords match
      if (newPassword !== confirmPassword) {
        setPasswordError("Passwords do not match");
        return;
      }

      const accessToken = await getAccessToken();
      const federatedUserId = user.federatedUserId;

      logger.log("[ChangePassword] Changing password for:", federatedUserId);

      const response = await fetch(
        `${
          config.authorizationApiEndpoint
        }/user/change-password?federatedUserId=${encodeURIComponent(
          federatedUserId
        )}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "x-api-key": config.apiKey,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            newPassword: newPassword,
            confirmPassword: confirmPassword,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to change password");
      }

      setPasswordSuccess(true);
      setNewPassword("");
      setConfirmPassword("");

      // Close modal after short delay
      setTimeout(() => {
        onClose();
        setPasswordSuccess(false);
      }, 2000);
    } catch (err) {
      logger.error("[ChangePassword] Error changing password:", err);
      setPasswordError(
        err.message || "Failed to change password. Please try again."
      );
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleClose = () => {
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError(null);
    setPasswordSuccess(false);
    onClose();
  };

  const handleKeyPress = (e) => {
    if (
      e.key === "Enter" &&
      !isChangingPassword &&
      newPassword &&
      confirmPassword
    ) {
      handleChangePassword();
    }
  };

  if (!isOpen) {
    return null;
  }

  const requirements = validatePassword(newPassword);
  const strength = newPassword ? getPasswordStrength(newPassword) : null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal-content change-password-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {isChangingPassword && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>Changing password...</p>
          </div>
        )}

        <div className="modal-header">
          <h2>Change Password</h2>
          <button
            className="close-button"
            onClick={handleClose}
            disabled={isChangingPassword}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {passwordError && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {passwordError}
            </div>
          )}

          {passwordSuccess && (
            <div className="success-message">
              <span className="success-icon">✓</span>
              Password changed successfully!
            </div>
          )}

          <div className="password-requirements">
            <p className="requirements-title">Password must contain:</p>
            <ul className="requirements-list">
              {requirements.map((req, index) => (
                <li
                  key={index}
                  className={req.test ? "requirement-met" : "requirement-unmet"}
                >
                  <span className="requirement-icon">
                    {req.test ? "✓" : "○"}
                  </span>
                  {req.message}
                </li>
              ))}
            </ul>
          </div>

          {strength && (
            <div className="password-strength">
              <span className="strength-label">Password strength:</span>
              <div className="strength-bar">
                <div className={`strength-fill strength-${strength}`}></div>
              </div>
              <span className={`strength-text strength-${strength}`}>
                {strength.charAt(0).toUpperCase() + strength.slice(1)}
              </span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="newPassword">
              New Password <span className="required">*</span>
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isChangingPassword}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              Confirm Password <span className="required">*</span>
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isChangingPassword}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
            {confirmPassword && confirmPassword === newPassword && (
              <span className="field-hint success-hint">✓ Passwords match</span>
            )}
            {confirmPassword && confirmPassword !== newPassword && (
              <span className="field-hint error-hint">
                ✗ Passwords do not match
              </span>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="button button-secondary"
            onClick={handleClose}
            disabled={isChangingPassword}
          >
            Cancel
          </button>
          <button
            className="button button-primary"
            onClick={handleChangePassword}
            disabled={isChangingPassword || !newPassword || !confirmPassword}
          >
            {isChangingPassword ? "Changing Password..." : "Change Password"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
